import { Link } from '@tanstack/react-router'
import BigNumber from 'bignumber.js'
import { Gift, LoaderCircle, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { type QrEntry, useQrPromo } from '@/providers/QrPromoProvider'
import { formatUsd, rewardView, safeKycUrl, targetEquity } from '@/services/qrPromo/model'

function deadline(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? `${date.toLocaleString(undefined, { timeZone: 'UTC', dateStyle: 'medium', timeStyle: 'short' })} UTC`
    : 'Check the current campaign deadline'
}
const actionClass =
  'inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-primary px-5 py-2 text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-50'

function QrParticipantBanner({ entry }: { entry: QrEntry }) {
  const promo = useQrPromo()!
  const { challenge, result, reward } = entry
  const [confirming, setConfirming] = useState(false)
  const [checking, setChecking] = useState(false)
  const [message, setMessage] = useState('')
  const pending = promo.requesting === challenge.id
  const funding = result.status === 'PENDING'
  const view = reward ? rewardView(reward) : undefined
  const progress =
    reward?.current_equity === undefined
      ? undefined
      : new BigNumber(reward.current_equity).div(targetEquity(reward)).times(100)
  const refresh = async () => {
    setChecking(true)
    setMessage('')
    try {
      return await promo.refresh(challenge.id)
    } catch {
      setMessage('Status could not be refreshed. Please try again before requesting a reward.')
      return undefined
    } finally {
      setChecking(false)
    }
  }
  const prepare = async () => {
    const latest = await refresh()
    if (latest?.state === 'ELIGIBLE' && !latest.blockers.length) setConfirming(true)
    else if (latest) setMessage('Your challenge status changed. Review the current status below.')
  }
  const submit = async () => {
    setConfirming(false)
    setMessage('')
    try {
      await promo.requestReward(challenge.id)
    } catch (error) {
      setMessage(
        `${error instanceof Error ? error.message : 'Request could not be confirmed.'} Your status has been refreshed where possible. A declined request may already have closed positions.`,
      )
    }
  }
  return (
    <section
      aria-label={`${challenge.name ?? 'Promotional challenge'} status`}
      aria-live="polite"
      className="border-y border-primary/30 bg-[linear-gradient(110deg,rgba(0,35,44,.96),rgba(3,17,24,.98))] px-4 py-4 text-white sm:px-6"
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          {pending || funding ? (
            <LoaderCircle className="size-10 shrink-0 animate-spin text-primary" aria-hidden />
          ) : (
            <Gift className="size-10 shrink-0 text-primary" aria-hidden />
          )}
          <div>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-primary">
              {challenge.name ?? 'QR promotional challenge'}
            </p>
            <h2 className="text-lg font-semibold">
              {pending
                ? 'Requesting reward — winding down trading'
                : funding
                  ? 'Preparing your promotional account'
                  : (view?.title ?? 'Loading reward status')}
            </h2>
            <p className="mt-1 max-w-2xl text-sm text-slate-300">
              {pending
                ? 'The server is closing positions, cancelling orders and locking trading. This may take a moment.'
                : funding
                  ? 'Setup and funding are processing. Trading will become available after funding and account availability are confirmed.'
                  : (view?.text ?? 'Refresh to check the current state of your challenge.')}
            </p>
            {result.deactivation_status === 'DEACTIVATED' && (
              <p className="mt-1 text-sm text-amber-200">
                Account trading is locked by the server.
              </p>
            )}
            {reward && (
              <p className="mt-2 text-xs text-slate-300">
                Reward range {formatUsd(reward.min_reward_amount)}–
                {formatUsd(reward.max_reward_amount)} · Request and verification deadline:{' '}
                {deadline(reward.reward_deadline_at)}
              </p>
            )}
          </div>
        </div>
        {reward && !funding && (
          <div className="min-w-44 shrink-0 lg:border-l lg:border-primary/30 lg:pl-6">
            <p className="text-xs uppercase text-slate-300">
              {reward.state === 'ELIGIBLE'
                ? 'Projected reward'
                : reward.state === 'NOT_ELIGIBLE'
                  ? 'Account equity / target'
                  : 'Fixed reward'}
            </p>
            <p className="mt-1 text-2xl font-semibold text-primary">
              {reward.state === 'NOT_ELIGIBLE' ? (
                <>
                  {formatUsd(reward.current_equity)}{' '}
                  <span className="text-base text-slate-300">
                    / {formatUsd(targetEquity(reward))}
                  </span>
                </>
              ) : (
                formatUsd(view?.amount)
              )}
            </p>
            {reward.state === 'NOT_ELIGIBLE' && progress?.isFinite() && (
              <progress
                aria-label="Target equity progress"
                className="mt-2 h-2 w-full accent-primary"
                max={100}
                value={Math.max(0, Math.min(100, progress.toNumber()))}
              />
            )}
          </div>
        )}
        <div className="flex shrink-0 flex-wrap items-center gap-3">
          {!funding && view?.action === 'claim' && (
            <button
              type="button"
              className={actionClass}
              disabled={
                pending ||
                checking ||
                entry.refreshing ||
                entry.error ||
                Boolean(promo.recoveryRequired)
              }
              onClick={() => void prepare()}
            >
              Request reward
            </button>
          )}
          {!funding && view?.action === 'kyc' && (
            <a
              href={safeKycUrl(reward?.kyc_url)}
              rel="noreferrer noopener"
              target="_blank"
              className={actionClass}
            >
              Complete verification
            </a>
          )}
          {view?.action === 'withdraw' && (
            <Link to="/portfolio" className={actionClass}>
              View balance & withdrawals
            </Link>
          )}
          <button
            type="button"
            aria-label="Refresh challenge status"
            disabled={checking || pending}
            onClick={() => void refresh()}
            className="inline-flex min-h-11 items-center gap-2 rounded-md border border-primary/40 px-3 text-sm disabled:opacity-50"
          >
            <RefreshCw className={`size-4 ${checking ? 'animate-spin' : ''}`} aria-hidden />
            Refresh status
          </button>
        </div>
      </div>
      {(message || entry.error || promo.recoveryRequired === challenge.id) && (
        <p role="alert" className="mt-3 text-sm text-amber-200">
          {message ||
            'Current status is unavailable. Refresh before making another reward request.'}
        </p>
      )}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request your promotional reward?</AlertDialogTitle>
            <AlertDialogDescription>
              The server will close all positions, cancel all orders and lock trading. Your
              projected reward is {formatUsd(reward?.projected_reward)}; it can change during
              wind-down. Only an accepted request fixes the final amount.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p className="text-sm">
            Complete identity verification by{' '}
            {reward ? deadline(reward.reward_deadline_at) : 'the campaign deadline'}. Requesting
            does not extend this deadline. Settlement happens after timely verification; an accepted
            request is not a payout.
          </p>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep trading</AlertDialogCancel>
            <button
              type="button"
              disabled={pending || checking}
              onClick={() => void submit()}
              className={actionClass}
            >
              Confirm reward request
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
export default function QrPromoStatusBanner() {
  const promo = useQrPromo()
  const [checking, setChecking] = useState(false)
  if (!promo) return null
  if (promo.error)
    return (
      <section role="status" className="border-b border-primary/30 p-3 text-sm">
        Promotional challenge status could not be loaded.{' '}
        <button
          type="button"
          disabled={checking}
          className="underline"
          onClick={() => {
            setChecking(true)
            void promo
              .refresh()
              .catch(() => {})
              .finally(() => setChecking(false))
          }}
        >
          Refresh status
        </button>
      </section>
    )
  return (
    <>
      {promo.entries.map((entry) => (
        <QrParticipantBanner key={entry.challenge.id} entry={entry} />
      ))}
    </>
  )
}
